// Copyright 2025 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { PositionedDeviceOfFrontEnd, Point3f, FrontEnd } from "datatypes/SoramaLocalApi";
import { HardwareServiceError } from "errors/HardwareServiceError";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { PromiseExtensions } from "extensions/PromiseExtensions";
import { ILocalApi } from "libraries/WebApi/ILocalApi";
import { IWebRequestHandler, WebRequestPriority } from "libraries/WebRequestHandler/IWebRequestHandler";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { IReservableHardwareService, IHardwareReserver } from "services/Hardware/IReservableHardwareService";
import { LocalHardwareService } from "services/Hardware/LocalHardwareService";
import { ExtendedLifestyleService } from "services/Lifestyle/ExtendedLifestyleService";

class ReservedDeviceConfiguration<THardware extends IHardwareDevice> {
    public reservationKey: string;
    public devices: THardware[];
}

class ReservedDeviceConfigurations<THardware extends IHardwareDevice> extends Array<
    ReservedDeviceConfiguration<THardware>
> {
    public get devices(): THardware[] {
        return this.reduce<THardware[]>((prev, curr) => prev.concat(curr.devices), []);
    }

    // See: https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    constructor(...items: ReservedDeviceConfiguration<THardware>[]) {
        const array = super(...items);
        Object.setPrototypeOf(array, ReservedDeviceConfigurations.prototype);
        return array as unknown as ReservedDeviceConfigurations<THardware>;
    }
}

@autoinject
export class ReservableHardwareService<THardware extends IHardwareDevice>
    extends LocalHardwareService<THardware>
    implements IReservableHardwareService<THardware, string>
{
    //#region Constants
    private static DeviceRefreshInterval = 4000;
    private defaultOnErrorRefreshInterval = 2000;
    //#endregion

    //#region Injected Services
    protected webRequestHandler: IWebRequestHandler;
    public baseUri: () => Promise<string>;
    //#endregion

    private deviceConfigurations: ReservedDeviceConfigurations<THardware>;
    private reservers: { [reservationKey: string]: IHardwareReserver<string>[] };

    private lifestyleService: ExtendedLifestyleService<string>;

    constructor(webRequestHandler: IWebRequestHandler, baseUri: () => Promise<string>) {
        super(webRequestHandler, baseUri);

        this.baseUri = baseUri;
        this.deviceConfigurations = new ReservedDeviceConfigurations<THardware>();
        this.reservers = {};

        // Create a service that will manage the lifetime for our devices
        this.lifestyleService = new ExtendedLifestyleService(
            (x: string) => this.onUpdateDevice(x),
            (x: string, e: HttpResponseMessageError) => this.onError(x, e),
            ReservableHardwareService.DeviceRefreshInterval
        );
    }

    public canReserve(devices: THardware[]): Promise<void> {
        return this.baseUri().then((uri) => {
            return this.webRequestHandler
                .createPostRequest<void>(uri + "/CanPostCollection", devices)
                .send()
                .then(() => {});
        });
    }

    public reserve(devices: THardware[], reserver: IHardwareReserver<string>, positions?: Point3f[]): Promise<string> {
        for (const device of devices) {
            const reservedHardware = this.deviceConfigurations.find((x) => x.devices.some((x) => x.id === device.id));

            if (reservedHardware) {
                const sameSet = ArrayExtensions.equals(
                    devices.map((x) => x.id).sort(),
                    reservedHardware.devices.map((x) => x.id).sort()
                );

                // First check if any of the the devices was already reserved and if the same set of devices has been reserved together
                if (sameSet) {
                    // Device set found, add reserver
                    this.reservers[reservedHardware.reservationKey].push(reserver);
                    return Promise.resolve(reservedHardware.reservationKey);
                } else {
                    return Promise.reject(
                        new Error("One or more devices are already reserved in a different configuration.")
                    );
                }
            }
        }

        const postDevices =
            positions && positions.length === devices.length
                ? devices.map(
                      (x, i) => <PositionedDeviceOfFrontEnd>{ device: <FrontEnd>(<unknown>x), position: positions[i] }
                  )
                : devices.map(
                      (x, i) =>
                          <PositionedDeviceOfFrontEnd>{
                              device: <FrontEnd>(<unknown>x),
                              position: <Point3f>{ X: 0, Y: 0, Z: 0 },
                          }
                  );

        // None of the devices was registered, register them together
        return this.baseUri().then((uri) => {
            return this.webRequestHandler
                .createPostRequest<string>(uri + "/PostCollection", postDevices)
                .send()
                .then((x) => {
                    const reservationKey = x.content;
                    this.deviceConfigurations.push({ reservationKey: reservationKey, devices: devices });
                    this.reservers[reservationKey] = [reserver];

                    // Tell the lifestyle service to keep the binding alive
                    this.lifestyleService.addKey(reservationKey);

                    return reservationKey;
                })
                .catch((e: HttpResponseMessageError) => {
                    throw new HardwareServiceError(e.message);
                });
        });
    }

    public release(reservationKey: string, reserver: IHardwareReserver<string>): Promise<void> {
        // Check if the device was reserved in the first place
        if (!Object.prototype.hasOwnProperty.call(this.reservers, reservationKey))
            return Promise.reject(new HardwareServiceError("Unknown device key"));

        // Check if the reserver exists in the list of reservers
        const reserversForDevice = this.reservers[reservationKey];
        const reserverIndex = reserversForDevice.indexOf(reserver);

        if (reserverIndex === -1) return Promise.reject(new HardwareServiceError("Unknown reserver"));

        // Remove reserver from set of reservers
        reserversForDevice.splice(reserverIndex, 1);

        // If this was the only reserver, release device on server
        if (reserversForDevice.length === 0) {
            // Remove device configuration
            this.removeDeviceConfigurationWithKey(reservationKey);

            // Finally release the device
            return this.baseUri().then((uri) => {
                return this.webRequestHandler
                    .createDeleteRequest<void>(uri + "/" + reservationKey)
                    .send()
                    .then(() => {})
                    .catch((e: HttpResponseMessageError) => {
                        throw new HardwareServiceError(e.message);
                    });
            });
        }

        // Return without releasing the device on the server
        return Promise.resolve();
    }

    public afterRefresh(devices: THardware[]): THardware[] {
        // Make sure that all the reserved devices are included
        for (const device of this.deviceConfigurations.devices) {
            if (devices.some((x) => x.id === device.id)) continue;

            // Device does not exists in the provided list, add it
            devices.push(device);
        }

        return devices;
    }

    private onUpdateDevice(deviceKey: string): Promise<void> {
        return this.baseUri().then((uri) => {
            return this.webRequestHandler
                .createPutRequest<void>(uri + "/" + deviceKey)
                .withPriority(WebRequestPriority.HighPriority)
                .send()
                .then(() => {});
        });
    }

    private onError(reservationKey: string, error: HttpResponseMessageError): void {
        // Remove the device from the reserved list, notify the reserver and trigger a hardware refresh
        if (Object.prototype.hasOwnProperty.call(this.reservers, reservationKey)) {
            const reservers = this.reservers[reservationKey];
            reservers.forEach((x) => x.onDeviceLost(reservationKey));
            delete this.reservers[reservationKey];
        }

        // Remove device configuration
        this.removeDeviceConfigurationWithKey(reservationKey);

        // Try again after a few seconds
        PromiseExtensions.delayedPromise(this.defaultOnErrorRefreshInterval).then(() => this.refresh());
    }

    private removeDeviceConfigurationWithKey(reservationKey: string) {
        // Tell the lifestyle service to stop updating the key
        this.lifestyleService.removeKey(reservationKey);

        // Remove from set of reservers
        delete this.reservers[reservationKey];

        // Remove from set of devices
        const deviceConfigurationIdx = this.deviceConfigurations.findIndex((x) => x.reservationKey === reservationKey);
        if (deviceConfigurationIdx !== -1) {
            this.deviceConfigurations.splice(deviceConfigurationIdx, 1);
        }
    }
}
